import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmergencyServiceComponent } from '../../components/ui/emergency-service/emergency-service.component';
import { DataService } from '../../services/data.service';
import { ContactInfo } from '../../models/contact';

type OpeningHours = ContactInfo['openingHours'];

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [CommonModule, EmergencyServiceComponent],
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss']
})
export class ContactComponent implements OnInit {
  contactInfo!: ContactInfo;

  // Index 0 = Sonntag, passend zu Date.getDay()
  private readonly dayKeys: (keyof OpeningHours)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
  ];
  private readonly dayLabels = [
    'Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'
  ];

  constructor(private dataService: DataService) {}

  ngOnInit(): void {
    this.dataService.getContactInfo().subscribe(info => {
      this.contactInfo = info;
    });
  }

  /** Sind wir laut Öffnungszeiten gerade regulär erreichbar? */
  get isOpenNow(): boolean {
    if (!this.contactInfo) {
      return false;
    }
    const now = new Date();
    const range = this.parseHours(
      this.contactInfo.openingHours[this.dayKeys[now.getDay()]]
    );
    if (!range) {
      return false;
    }
    const minutes = now.getHours() * 60 + now.getMinutes();
    return minutes >= range.start && minutes < range.end;
  }

  /**
   * Soll die Notdienst-Nummer aktuell angezeigt werden?
   * Werktags nur von 18:00 bis 6:00 Uhr, am Wochenende durchgehend.
   */
  get showEmergency(): boolean {
    const now = new Date();
    const day = now.getDay(); // 0 = Sonntag, 6 = Samstag
    if (day === 0 || day === 6) {
      return true;
    }
    const hour = now.getHours();
    return hour >= 18 || hour < 6;
  }

  /** Hinweis, wann die regulären Zeiten wieder beginnen. */
  get nextOpeningText(): string {
    if (!this.contactInfo) {
      return '';
    }
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    for (let offset = 0; offset < 8; offset++) {
      const index = (now.getDay() + offset) % 7;
      const range = this.parseHours(this.contactInfo.openingHours[this.dayKeys[index]]);
      if (!range) {
        continue;
      }
      // Heute, aber die Öffnungszeit liegt schon in der Vergangenheit
      if (offset === 0 && nowMinutes >= range.start) {
        continue;
      }

      const time = this.formatMinutes(range.start);
      if (offset === 0) {
        return `Heute wieder ab ${time} Uhr erreichbar`;
      }
      if (offset === 1) {
        return `Wieder erreichbar ab morgen, ${time} Uhr`;
      }
      return `Wieder erreichbar ab ${this.dayLabels[index]}, ${time} Uhr`;
    }
    return '';
  }

  getOpeningHoursArray(): { day: string; hours: string; isToday: boolean }[] {
    if (!this.contactInfo) {
      return [];
    }
    const todayKey = this.dayKeys[new Date().getDay()];

    const days: { key: keyof OpeningHours; label: string }[] = [
      { key: 'monday', label: 'Montag' },
      { key: 'tuesday', label: 'Dienstag' },
      { key: 'wednesday', label: 'Mittwoch' },
      { key: 'thursday', label: 'Donnerstag' },
      { key: 'friday', label: 'Freitag' },
      { key: 'saturday', label: 'Samstag' },
      { key: 'sunday', label: 'Sonntag' }
    ];

    return days.map(day => ({
      day: day.label,
      hours: this.contactInfo.openingHours[day.key],
      isToday: day.key === todayKey
    }));
  }

  /** Wandelt eine angezeigte Rufnummer in einen tel:-konformen Wert um. */
  toTelHref(phone: string): string {
    const digits = (phone || '').replace(/\D/g, '');
    return digits.startsWith('0') ? '+49' + digits.slice(1) : digits;
  }

  /** Parst Strings wie "8:00 - 15:30" zu Minutenwerten; null bei "Geschlossen". */
  private parseHours(value: string): { start: number; end: number } | null {
    const match = (value || '').match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
    if (!match) {
      return null;
    }
    return {
      start: +match[1] * 60 + +match[2],
      end: +match[3] * 60 + +match[4]
    };
  }

  private formatMinutes(total: number): string {
    const hours = Math.floor(total / 60);
    const minutes = total % 60;
    return minutes === 0 ? `${hours}:00` : `${hours}:${`${minutes}`.padStart(2, '0')}`;
  }
}
